import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer } from '../components/Toast';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 5000, options = {}) => {
    const id = Date.now() + Math.random();
    const toast = { 
      id, 
      message, 
      type, 
      duration,
      actions: options.actions || [],
      persistent: options.persistent || false
    };
    
    setToasts(prev => [...prev, toast]);
    
    // Auto-remove toast after duration (unless persistent or has actions)
    if (duration > 0 && !toast.persistent && (!toast.actions || toast.actions.length === 0)) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message, duration, options) => addToast(message, 'success', duration, options), [addToast]);
  const showError = useCallback((message, duration, options) => addToast(message, 'error', duration, options), [addToast]);
  const showWarning = useCallback((message, duration, options) => addToast(message, 'warning', duration, options), [addToast]);
  const showInfo = useCallback((message, duration, options) => addToast(message, 'info', duration, options), [addToast]);

  // Generic showToast method that maps to specific methods based on type
  const showToast = useCallback((message, type = 'info', duration, options) => {
    switch (type) {
      case 'success':
        return showSuccess(message, duration, options);
      case 'error':
        return showError(message, duration, options);
      case 'warning':
        return showWarning(message, duration, options);
      case 'info':
      default:
        return showInfo(message, duration, options);
    }
  }, [showSuccess, showError, showWarning, showInfo]);

  // Backward-compatible API: expose both `showX` and shorthand `x` methods
  const value = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    // Preferred explicit methods
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showToast,
    // Aliases for legacy usage (toast.success, toast.error, ...)
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};
